import { Button, FormControl, FormControlLabel, Radio, RadioGroup } from "@material-ui/core";
import { observer } from "mobx-react-lite";
import React from "react";

import { DarwinSelect } from "Components/AutoComplete/DarwinSelect";
import { BaseModal } from "Components/Modal/BaseModal";
import { SnackBar } from "Components/SnackBar/SnackBar";
import { PlanOfWorkStage } from "Globals/Models/Enums/PlanOfWorkStage";
import { CancelButton } from "Globals/Styles/Control/Buttons";
import { AddPlanOfWorkModalStyle } from "Views/Project/Modals/Modal.Styles";
import { FormModalSection, FormRow } from "Views/Stock/Shared/Shared.styles";
import { Checkbox } from "@material-ui/core";
import { AddPlanOfWorkViewModel, IPlanOfWorkDrawing } from ".";
import { IssueOutputDisplayName, OutputDrawing, OutputDrawingContainer } from "./AddPlanOfWorkView.styles";
import { PlansOfWorkItemViewModel, PlansOfWorkViewModel } from "../Table";
import { DownloadDrawingView } from "../../Shared";

interface IProps {
    viewModel: AddPlanOfWorkViewModel;
    parentViewModel: PlansOfWorkViewModel;
}

export const AddPlanOfWorkView: React.FC<IProps> = observer(({ viewModel, parentViewModel }) => {
    return (
        <BaseModal
            open={viewModel !== null}
            onClose={viewModel.cancel}
            title="New RIBA stage"
            PaperComponent={AddPlanOfWorkModalStyle}
            actions={
                <>
                    <Button autoFocus color="secondary" disabled={viewModel.server.IsBusy} onClick={viewModel.onSave} variant="contained">
                        Save
                    </Button>
                    <CancelButton onClick={viewModel.cancel}>Cancel</CancelButton>
                </>
            }
        >
            <FormModalSection>
                <FormRow grid dc={"1fr"}>
                    <FormControl>
                        <RadioGroup row value={viewModel.model.mode} onChange={(e) => viewModel.setMode(e.target.value as any)}>
                            <FormControlLabel value="create" control={<Radio />} label="New RIBA stage" />
                            <FormControlLabel value="copy" control={<Radio />} label="Copy existing RIBA stage" />
                        </RadioGroup>
                    </FormControl>
                </FormRow>

                {viewModel.model.mode === "create" && (
                    <FormRow grid dc={"1fr"}>
                        <DarwinSelect
                            displayName="RIBA stage:"
                            execute={viewModel.setPlanOfWorkStage}
                            fullWidth
                            getOptionLabel={(option: PlanOfWorkStage) => option?.displayName ?? ""}
                            options={viewModel.planOfWorkStages}
                            placeholder="Please select"
                            validationMessage={viewModel.getError("planOfWorkStage")}
                            value={viewModel.planOfWorkStage}
                        />
                    </FormRow>
                )}

                {viewModel.model.mode === "copy" && (
                    <>
                        <FormRow grid dc={"1fr"}>
                            <DarwinSelect
                                displayName="New RIBA Stage:"
                                execute={viewModel.setPlanOfWorkStage}
                                fullWidth
                                getOptionLabel={(option: PlanOfWorkStage) => option?.displayName ?? ""}
                                options={viewModel.planOfWorkStages}
                                placeholder="Please select"
                                validationMessage={viewModel.getError("planOfWorkStage")}
                                value={viewModel.planOfWorkStage}
                            />
                        </FormRow>
                        {viewModel.model.planOfWorkStage != null && (
                            <>
                                <FormRow grid dc={"1fr"}>
                                    <DarwinSelect
                                        displayName="Select existing RIBA stage:"
                                        execute={viewModel.setPlanToCopyFrom}
                                        fullWidth
                                        getOptionLabel={(option: PlansOfWorkItemViewModel) => option.name}
                                        options={parentViewModel.getPlanOfWorkOptions}
                                        placeholder="Select a stage to copy"
                                        value={viewModel.model.planToCopyFrom}
                                    />
                                </FormRow>
                                {viewModel.outputs.map((output: { id: string; name: string }) => (
                                    <>
                                        <IssueOutputDisplayName>{output.name}</IssueOutputDisplayName>
                                        <OutputDrawingContainer>
                                            {viewModel.availableDrawings
                                                .filter((d) => d.outputId === output.id)
                                                .map((drawing: IPlanOfWorkDrawing) => (
                                                    <OutputDrawing>
                                                        <Checkbox checked={viewModel.isDrawingSelected(drawing)} onClick={() => viewModel.toggleDrawingSelection(drawing)} />
                                                        <DownloadDrawingView
                                                            fileName={drawing.drawingFileName}
                                                            attachmentUrl={drawing.drawingAttachmentUrl}
                                                            inlineUrl={drawing.drawingInlineUrl}
                                                            autodeskVersionId={drawing.autodeskVersionId}
                                                            drawingId={drawing.drawingId}
                                                        />
                                                    </OutputDrawing>
                                                ))}
                                        </OutputDrawingContainer>
                                    </>
                                ))}
                            </>
                        )}
                    </>
                )}
            </FormModalSection>

            <SnackBar
                messageText={viewModel.snackMessage}
                messageType={viewModel.snackType}
                active={viewModel.snackbarState}
                closeOption={() => viewModel.setSnackbarState(false)}
                autoHideDuration={5000}
            />
        </BaseModal>
    );
});
